import { WorkflowStepTag } from "@/features/workflows/components/workflow-step-tag";
import { formatLastNChars } from "@/shared/utils/uuid-formatter";
import type {
  ControlResultColumnResponseDto,
  ControlResultCurrencyRelationshipDto,
  ControlResultListItemResponseDto,
  OwnerResponseDto,
  PriorityResponseDto,
  WorkflowStepResponseDto,
} from "@/spz-api/model";
import { Avatar, AvatarFallback, AvatarImage } from "@spz/ui/components/avatar";
import { Checkbox } from "@spz/ui/components/checkbox";
import Icon from "@spz/ui/components/icon";
import { PriorityBadge } from "@spz/ui/components/priority-badge";
import { EmptyCell } from "@spz/ui/components/table/cells/EmptyCell";
import { ConclusionCell } from "@spz/ui/components/table/cells/conclusion-cell";
import { TextCell } from "@spz/ui/components/table/cells/text-cell";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useBulkActionStore } from "../store/bulk-action.store";
import { useBulkSelectionStore } from "../store/bulk-selection.store";
import { formatCellValue } from "../utils/format-cell-value";

export type ControlResultWithRelationships =
  ControlResultListItemResponseDto & {
    owner?: OwnerResponseDto;
    currentWorkflowStep?: WorkflowStepResponseDto;
    associatedCurrency?: ControlResultCurrencyRelationshipDto;
    priority?: PriorityResponseDto;
  };
export const useControlResultsColumns = ({
  controlResultColumns,
}: {
  controlResultColumns: ControlResultColumnResponseDto[];
}) => {
  const { t, i18n } = useTranslation();
  const columnHelper = createColumnHelper<ControlResultWithRelationships>();
  const {
    selectionEnabled,
    isRowSelected,
    getHeaderCheckboxState,
    toggleRow,
    togglePage,
  } = useBulkSelectionStore();
  const { isBulkActionInProgress } = useBulkActionStore();

  const selectionColumn = selectionEnabled
    ? columnHelper.display({
        id: "selection",
        header: ({ table }) => {
          const pageRowIds = table.getRowModel().rows.map((r) => String(r.id));
          const state = getHeaderCheckboxState(pageRowIds);
          const checked =
            state === "checked"
              ? true
              : state === "indeterminate"
                ? "indeterminate"
                : false;

          return (
            <Checkbox
              aria-label={t("select_all_on_page")}
              checked={checked}
              disabled={isBulkActionInProgress}
              onClick={(e) => {
                e.stopPropagation();
                const shouldSelect = state !== "checked";
                togglePage(pageRowIds, shouldSelect);
                table.toggleAllPageRowsSelected(shouldSelect);
              }}
            />
          );
        },
        cell: ({ row, table }) => {
          const id = String(row.id);
          const checked = isRowSelected(id);

          return (
            <Checkbox
              aria-label={t("select_row")}
              checked={checked}
              disabled={isBulkActionInProgress}
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(
                  id,
                  !checked,
                  table.getRowModel().rows.map((r) => String(r.id)),
                );
                row.toggleSelected(!checked);
              }}
            />
          );
        },
        size: 40,
        enableResizing: false,
        enableHiding: false,
      })
    : null;
  const dynamicDataTableColumns = controlResultColumns.map((item) => {
    const accessorKey = item.columnKey;

    return columnHelper.accessor(
      (row) =>
        formatCellValue(
          row.data,
          item.type,
          accessorKey,
          t,
          i18n,
          row.associatedCurrency?.minorUnit,
          row.associatedCurrency?.alphabeticalCode,
        ),
      {
        id: accessorKey,
        size: 150,
        minSize: 100,
        header: t(item.title),
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return value ? <TextCell value={String(value)} /> : <EmptyCell />;
        },
      },
    );
  });

  return [
    ...(selectionColumn ? [selectionColumn] : []),
    columnHelper.accessor("resultGroupId", {
      id: "resultGroupId",
      header: t("result_id"),
      cell: (info) => formatLastNChars(info.getValue(), 8),
      size: 120,
      minSize: 80,
    }),
    columnHelper.accessor("currentWorkflowStepId", {
      id: "workflow_step_uuid",
      header: t("column_status"),
      cell: (info) => {
        const currentWorkflowStep = info.row.original?.currentWorkflowStep;
        if (!currentWorkflowStep) {
          return <EmptyCell />;
        }
        return <WorkflowStepTag workflowStep={currentWorkflowStep} />;
      },
      size: 150,
      minSize: 120,
      filterFn: "arrIncludesSome",
    }),
    columnHelper.accessor(
      (row: ControlResultWithRelationships) =>
        row.conclusion?.labelKeyName
          ? t(row.conclusion.labelKeyName)
          : (row.conclusion?.name ?? ""),
      {
        id: "conclusion_uuid",
        header: t("conclusion"),
        cell: (info) => <ConclusionCell value={info.getValue()} />,
        size: 150,
        minSize: 120,
        filterFn: "arrIncludesSome",
      },
    ),

    columnHelper.accessor(
      (row: ControlResultWithRelationships) =>
        row.category?.labelKeyName
          ? t(row.category.labelKeyName)
          : (row.category?.name ?? ""),
      {
        id: "conclusion_category_uuid",
        header: t("conclusion"),
        cell: (info) => <ConclusionCell value={info.getValue()} />,
        size: 150,
        minSize: 120,
        filterFn: "arrIncludesSome",
      },
    ),
    columnHelper.accessor(
      (row: ControlResultWithRelationships) =>
        row.priority?.labelKeyName ? t(row.priority?.labelKeyName) : "",
      {
        id: "priority_uuid",
        header: t("priority"),
        cell: (info) => {
          const priority = info.row.original?.priority;
          if (!priority) {
            return (
              <span className="flex justify-left text-gray-400 mx-auto">-</span>
            );
          }
          return (
            <PriorityBadge
              id={priority.id}
              variant={priority.variant}
              symbol={priority.symbolKeyName}
              label={priority.labelKeyName}
            />
          );
        },
        size: 150,
        minSize: 120,
      },
    ),
    ...dynamicDataTableColumns,
    columnHelper.accessor("ownerId", {
      id: "owner_uuid",
      cell: (info) => {
        const owner = info.row.original?.owner;

        if (!owner) {
          return (
            <div className="flex items-center justify-center text-gray-900 w-6 h-6">
              <Icon icon="user" size={12} />
            </div>
          );
        }

        const fullName =
          `${owner?.firstName ?? ""} ${owner?.lastName ?? ""}`.trim();

        return (
          <Avatar size="M" fullName={fullName} enableTooltip>
            <AvatarImage src={owner?.avatarUrl} alt={`avatar of ${fullName}`} />
            <AvatarFallback
              size="M"
              firstName={owner?.firstName}
              lastName={owner?.lastName}
            />
          </Avatar>
        );
      },
      header: t("owner"),
      size: 130,
      minSize: 80,
    }),
  ];
};
